import { useAuth0 } from '@auth0/auth0-react';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIsDesktop } from '../../hooks/useIsDesktop';

import { AuthLayout } from './components/AuthLayout';

import icGdt from '@/assets/ic_gdt-white.svg';
import { Button } from '@/components';
import { AdSlot } from '@/components/GoogleAds/AdSlot';
import { useGoogleAds } from '@/hooks/useGoogleAds';
import { usePermutive } from '@/hooks/usePermutive';
import useToast from '@/hooks/useToast';
import ROUTES from '@/router/routes';
import {
  enableZondaAuth,
  zondaLogin,
  zondaSetupGoogleOneTap,
} from '@/services/zonda';
import { recessPosterAtom } from '@/store';
import { ADS_DIV_ID, SECTION_NAME_FOR_ADS } from '@/utils/ads';
import { tracking } from '@/utils/lib';
import { notifyNative } from '@/utils/nativeBridge';

export const Login = () => {
  const { loginWithRedirect } = useAuth0();
  const [recessPoster] = useAtom(recessPosterAtom);
  const navigate = useNavigate();
  const toast = useToast();
  useGoogleAds({ adsSections: SECTION_NAME_FOR_ADS.login });
  const { trackPageView, isReady: isPermutiveReady } = usePermutive();
  const { isDesktop } = useIsDesktop();
  const { trackRegistration } = tracking();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.login,
      });
      trackRegistration({
        modo: 'NO_ES_REGISTRACION',
        paseId: '-2',
        edadDt: -2,
        cdSexo: '-2',
        usuario: '-2',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  useEffect(() => {
    notifyNative('login-shown');
  }, []);

  useEffect(() => {
    if (enableZondaAuth) {
      zondaSetupGoogleOneTap();
    }
  }, []);

  function checkRecess(fnContinuation: () => void) {
    if (recessPoster?.estado && recessPoster.hayPlaca) {
      toast.show(
        recessPoster.mensajePlaca ||
          'Gran DT no está disponible en este momento.\n Por favor, volvé más tarde.'
      );
    } else {
      fnContinuation();
    }
  }

  return (
    <AuthLayout isLogin={true}>
      <div className="flex flex-col items-center lg:items-start lg:flex-row text-white lg:justify-between lg:w-[46.85rem] lg:mx-auto py-10">
        {/* visible on desktop */}
        {isDesktop && (
          <div className="hidden lg:block min-w-[18.75rem] max-w-[18.75rem]">
            <h3 className="text-lg text-gdt-gray-7 font-extrabold">
              ¿QUÉ ES GRAN DT?
            </h3>

            <p className="text-gdt-gray-7 font-normal mt-2.5 tracking-wide">
              Es un torneo de fútbol online. <br />
              Creá una cuenta, armá tu equipo <br />
              y participá del juego que apasiona <br />a todos los futboleros.
            </p>

            <Button
              className="text-white mt-3 underline-offset-4 hover:text-gray-300"
              id="linkFaqLogin"
              type="button"
              variant="text"
              onClick={() => window.open(ROUTES.preguntasFrecuentes)}
            >
              Más información
            </Button>

            <AdSlot className="mt-11" divId={ADS_DIV_ID.caja1} variant="BOX" />
          </div>
        )}

        <div className="flex flex-col max-w-[19.5rem] w-full mt-0">
          <figure className="mx-auto">
            <img alt="Logo Gran DT" className="w-28 lg:w-24 mb-4" src={icGdt} />
          </figure>

          <h1 className="text-center font-bebas text-3xl mt-14 leading-[35.2px] lg:mt-[2.19rem] font-normal">
            BIENVENIDO A GRAN DT
          </h1>

          <div className="text-center">
            <h2 className="text-center max-w-[17rem] xs:max-w-[100%] mx-auto mt-4 lg:hidden text-sm font-normal">
              Armá tu equipo y participá del juego que apasiona a todos los
              futboleros.
            </h2>

            <Button
              className="mt-9 w-full lg:mt-[3.38rem]"
              id="btnLogin"
              type="button"
              variant="primary"
              onClick={() =>
                checkRecess(() => {
                  if (enableZondaAuth) {
                    zondaLogin();
                    return;
                  }
                  loginWithRedirect({
                    authorizationParams: { screen_hint: 'login' },
                  });
                })
              }
            >
              Iniciar sesión
            </Button>
            <Button
              className="mt-3 w-full lg:mt-[1.12rem]"
              id="btnRegister"
              type="button"
              variant="secondary"
              onClick={() =>
                checkRecess(() => {
                  if (enableZondaAuth) {
                    zondaLogin();
                    return;
                  }
                  loginWithRedirect({
                    authorizationParams: { screen_hint: 'signup' },
                  });
                })
              }
            >
              Crear cuenta
            </Button>
            <Button
              className="text-white mt-6 lg:mt-[1.87rem] underline-offset-[.5rem] hover:text-gray-300 tracking-[.04rem]"
              id="btnRecoverEmail"
              type="button"
              variant="text"
              onClick={() =>
                checkRecess(() => {
                  navigate(ROUTES.recoverEmail);
                })
              }
            >
              No recuerdo mi e-mail
            </Button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};
